<template>
  <div class="w-full flex flex-col gap-9">
    <!-- Overview Section -->
    <div class="flex flex-col gap-4 w-full pt-8 lg:pt-0">
      <div class="flex items-center gap-2 px-1 py-1 lg:px-0 lg:py-0">
        <h1 class="text-text-primary text-3xl font-semibold">{{ $t('common.overview') }}</h1>
        <Info
          :title="$t('common.overview')"
          :description="$t('coach.match_overview.info_description', { rank: rankComparisonLabel })"
        />
      </div>

      <!-- User Stats Grid: coach card + two lowest stats for roster members, two lowest + K/D/A for everyone else -->
      <div class="flex flex-col gap-4" v-if="coachCardShown || hasLowestStats">
        <!-- The coach card spans a second row that holds only its reasoning (subgrid, see the card), so
             opening it grows the card alone; no row gap, the card spaces the two rows itself -->
        <div class="grid grid-cols-1 lg:grid-cols-3 w-full gap-6" :class="coachCardShown ? 'lg:gap-y-0' : ''">
          <CoachFeedbackCard
            v-if="coachCardShown"
            :feedback="props.coachingFeedback"
            :access-level="props.accessLevel"
            :viewer-played="props.viewerPlayed"
            :feedback-state="props.matchData?.coaching_feedback_state"
            :match-id="props.matchData?.uuid"
            @requested="emit('feedbackRequested')"
          />
          <StatBlock
            v-for="stat in props.activeUserTwoLowest"
            :key="stat.title"
            :stat="stat"
            @open-server-modal="openServerModal"
          />
          <div v-if="!coachCardShown" class="flex flex-col divide-y divide-gray-950 bg-gray-900 rounded-lg">
            <div class="px-5 py-4 lg:px-4 lg:py-3 text-text-primary">K/D/A</div>
            <div class="p-4 flex gap-2 items-center">
              <h2 class="text-text-secondary flex-1">{{ $t('coach.stat_labels.kills') }}</h2>
              <h1 class="text-text-primary text-lg font-medium">
                {{ props.activeUserMatchData?.kills?.value }}
              </h1>
            </div>
            <div class="p-4 flex gap-2 items-center">
              <h2 class="text-text-secondary flex-1">{{ $t('coach.stat_labels.assists') }}</h2>
              <h1 class="text-text-primary text-lg font-medium">
                {{ props.activeUserMatchData?.assists?.value }}
              </h1>
            </div>
            <div class="p-4 flex gap-2 items-center">
              <h2 class="text-text-secondary flex-1">{{ $t('coach.stat_labels.deaths') }}</h2>
              <h1 class="text-text-primary text-lg font-medium">
                {{ props.activeUserMatchData?.deaths?.value }}
              </h1>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Scoreboard Section -->
    <div class="flex flex-col gap-4 w-full">
      <!-- Desktop Filters -->
      <div class="items-center gap-3 hidden lg:flex">
        <h1 class="text-text-primary text-2xl font-semibold flex-1">{{ $t('coach.match_overview.scoreboard') }}</h1>

        <!-- Team Filter -->
        <FilterDropdown
          :model-value="selectedTeams"
          :options="teamOptions"
          :label="$t('coach.match_overview.team_filter')"
          @update="updateSelectedTeams"
        />

        <!-- Stat Type Filter -->
        <FilterDropdown
          :model-value="selectedStats"
          :options="statTypeOptions"
          :label="$t('coach.match_overview.type_filter')"
          @update="updateSelectedStats"
        />
      </div>

      <!-- Mobile Filters -->
      <div class="flex flex-col gap-3 lg:hidden">
        <h1 class="text-text-primary text-2xl font-semibold px-1 py-1 lg:px-0 lg:py-0">
          {{ $t('coach.match_overview.scoreboard') }}
        </h1>

        <div class="flex flex-row bg-gray-800 p-1 rounded-md">
          <div
            v-for="option in teamOptions"
            :key="option.key"
            @click="updateSelectedTeams(option.key)"
            class="w-full flex gap-2 text-sm items-center justify-center rounded-md py-1 px-2 cursor-pointer whitespace-nowrap"
            :class="[
              selectedTeams === option.key
                ? 'bg-gray-700 text-text-primary'
                : 'text-text-secondary bg-gray-800',
            ]"
          >
            {{ option.label }}
          </div>
        </div>

        <div class="flex flex-row bg-gray-800 p-1 rounded-md">
          <div
            v-for="option in statTypeOptions"
            :key="option.key"
            @click="updateSelectedStats(option.key)"
            class="w-full flex gap-2 text-sm items-center justify-center rounded-md py-1 px-2 cursor-pointer whitespace-nowrap"
            :class="[
              selectedStats === option.key
                ? 'bg-gray-700 text-text-primary'
                : 'text-text-secondary bg-gray-800',
            ]"
          >
            {{ option.label }}
          </div>
        </div>
      </div>

      <!-- Stats Tables -->
      <TransitionGroup tag="div" name="team-swap" class="flex flex-col gap-4 relative">
        <StatsTable
          v-for="team in orderedTeams"
          :key="team.id"
          :players="team.players"
          :columns="currentColumns"
          :team-label="team.label"
          :sort-column="sortColumn"
          :sort-direction="sortDirection"
          :platform="matchPlatform"
          :match-type="props.matchData?.match_type"
          :cs-map="props.matchData?.map_name"
          :current-user-id="user?.steam_id"
          :average-stats="props.matchData?.average_stats"
          @sort="handleColumnSort"
        />
      </TransitionGroup>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Info from '../../../General/Info.vue';
import StatBlock from '../filters/StatBlock.vue';
import FilterDropdown from '../filters/FilterDropdown.vue';
import StatsTable from '../tables/StatsTable.vue';
import CoachFeedbackCard from '../display/CoachFeedbackCard.vue';
import { matchPlatformOf, rankComparisonPlatformLabel } from '../../../../utils/matchPlatform.js';
import { escapeHtml } from '../../../../utils/pro_series_match.js';

const { t } = useI18n();

// `label` here is either a literal abbreviation (left untranslated) or a key under coach.*;
// `title` tooltips are always coach.column_titles.<key>.
const COLUMN_DEFS = {
  general: [
    {
      key: 'refrag_rating', label: 'coach.stat_labels.refrag_rating', sortable: true, formatter: 'decimal', decimalPlaces: 2,
    },
    {
      key: 'kills', label: 'K / D / A', sortable: true, formatter: 'kda', displayKey: 'kills',
    },
    {
      key: 'kdr', label: 'KDR', sortable: true, formatter: 'decimal', decimalPlaces: 2, displayKey: 'kdr',
    },
    {
      key: 'headshot_kill_percentage', label: 'coach.stat_labels.headshot_kill_percentage', sortable: true, formatter: 'percentage', multiplier: 100,
    },
    {
      key: 'kpr', label: 'KPR', sortable: true, formatter: 'decimal', decimalPlaces: 2,
    },
    {
      key: 'adr', label: 'ADR', sortable: true, formatter: 'integer',
    },
    {
      key: 'kast', label: 'KAST', sortable: true, formatter: 'decimal', decimalPlaces: 2,
    },
  ],
  aim: [
    {
      key: 'time_to_kill_average', label: 'TTK', sortable: true, formatter: 'seconds', unit: 's',
    },
    {
      key: 'time_to_damage_average', label: 'TTD', sortable: true, formatter: 'seconds', unit: 's',
    },
    {
      key: 'spotted_hit_rate', label: 'coach.column_labels.spotted_hit_rate', sortable: true, formatter: 'percentage', multiplier: 100,
    },
    {
      key: 'accurate_shots_while_counterstrafing_rate', label: 'coach.column_labels.accurate_shots_while_counterstrafing_rate', sortable: true, formatter: 'percentage', multiplier: 100,
    },
    {
      key: 'shots_at_low_velocity_rate', label: 'coach.column_labels.shots_at_low_velocity_rate', sortable: true, formatter: 'percentage', multiplier: 100,
    },
    {
      key: 'velocity_control_rate', label: 'coach.column_labels.velocity_control_rate', sortable: true, formatter: 'percentage', multiplier: 100,
    },
    {
      key: 'headshot_percentage', label: 'HS %', sortable: true, formatter: 'percentage', multiplier: 100,
    },
    {
      key: 'crosshair_placement_average', label: 'coach.stat_labels.crosshair_placement_average', sortable: true, formatter: 'decimal', decimalPlaces: 2, unit: '°',
    },
    {
      key: 'spray_shot_accuracy', label: 'coach.column_labels.spray_shot_accuracy', sortable: true, formatter: 'percentage', multiplier: 100,
    },
  ],
  trades: [
    {
      key: 'opening_successes', label: 'coach.column_labels.opening_successes', sortable: true, formatter: 'integer',
    },
    {
      key: 'opening_fails', label: 'coach.column_labels.opening_fails', sortable: true, formatter: 'integer',
    },
    {
      key: 'trade_kill_successes', label: 'coach.column_labels.trade_kill_successes', sortable: true, formatter: 'integer',
    },
    {
      key: 'trade_kill_fails', label: 'coach.column_labels.trade_kill_fails', sortable: true, formatter: 'integer',
    },
    {
      key: 'traded_death_successes', label: 'coach.column_labels.traded_death_successes', sortable: true, formatter: 'integer',
    },
    {
      key: 'traded_death_fails', label: 'coach.stat_labels.traded_death_fails', sortable: true, formatter: 'integer',
    },
  ],
  utility: [
    {
      key: 'util_flash_assists', label: 'coach.stat_labels.util_flash_assists', sortable: true, formatter: 'integer',
    },
    {
      key: 'util_flash_enemies_flashed', label: 'coach.stat_labels.util_flash_enemies_flashed', sortable: true, formatter: 'integer',
    },
    {
      key: 'util_flash_friends_flashed', label: 'coach.stat_labels.util_flash_friends_flashed', sortable: true, formatter: 'integer',
    },
    {
      key: 'average_enemy_flash_time', label: 'coach.column_labels.average_enemy_flash_time', sortable: true, formatter: 'seconds', unit: 's',
    },
    {
      key: 'util_enemy_damage', label: 'coach.column_labels.util_enemy_damage', sortable: true, formatter: 'integer',
    },
    {
      key: 'util_lost_cost', label: 'coach.column_labels.util_lost_cost', sortable: true, formatter: 'currency', prefix: '$',
    },
  ],
};

const STAT_TYPE_NAMES = {
  general: 'coach.stat_groups.general',
  aim: 'coach.stat_groups.aim',
  trades: 'coach.stat_groups.entry_trades',
  utility: 'coach.stat_groups.utility',
};

const MATCH_STATS_CONFIG = computed(() => ({
  statTypes: Object.fromEntries(Object.entries(COLUMN_DEFS).map(([type, columns]) => [type, {
    name: t(STAT_TYPE_NAMES[type]),
    columns: columns.map((column) => ({
      ...column,
      label: column.label.startsWith('coach.') ? t(column.label) : column.label,
      title: t(`coach.column_titles.${column.key}`),
    })),
  }])),
  teamOptions: [
    { key: 'all', label: t('common.all') },
    { key: 'winningteam', label: t('coach.match_overview.winning_team') },
    { key: 'losingteam', label: t('coach.match_overview.losing_team') },
  ],
}));

const props = defineProps({
  matchData: {
    type: Object,
    required: true,
  },
  isProSeries: {
    type: Boolean,
    default: false,
  },
  seriesTeams: {
    // Full series teams object: { team1: { name, players: [...] }, team2: {...} }
    type: Object,
    default: null,
  },
  teamNames: {
    type: Array,
    default: () => [],
  },
  // { a, b } team names for the two sides (pro series); HTML-escaped, StatsTable uses v-html
  sideLabels: {
    type: Object,
    default: null,
  },
  activeUserData: {
    type: Object,
    default: null,
  },
  activeUserMatchData: {
    type: Object,
    default: null,
  },
  activeUserTwoLowest: {
    type: Array,
    default: () => [],
  },
  mapScoreInfo: {
    type: Array,
    default: null,
  },
  userHighlights: {
    type: Array,
    default: () => [],
  },
  coachFeedbackEnabled: {
    type: Boolean,
    default: false,
  },
  // coaching_feedback from /coach/matches/:id (null until the worker has written it)
  coachingFeedback: {
    type: Object,
    default: null,
  },
  accessLevel: {
    type: Number,
    default: 0,
  },
  viewerPlayed: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['updateTab', 'openServerModal', 'feedbackRequested']);

// Feedback is per player, so the card only exists for a viewer on the roster;
// it then takes the K/D/A slot, a third of the row like the two stat blocks.
const coachCardShown = computed(() => props.coachFeedbackEnabled && props.viewerPlayed);
const hasLowestStats = computed(
  () => Boolean(props.activeUserMatchData) && props.activeUserTwoLowest?.length > 0,
);

const matchPlatform = computed(() => matchPlatformOf(props.matchData));

const rankComparisonLabel = computed(() => rankComparisonPlatformLabel(matchPlatform.value, props.matchData?.match_type));

const store = useStore();
const user = computed(() => store.state.user);

// State
const selectedTeams = ref('all');
const selectedStats = ref('general');
const sortColumn = ref('refrag_rating');
const sortDirection = ref('desc'); // "asc" or "desc"

// Computed
const teamOptions = computed(() => MATCH_STATS_CONFIG.value.teamOptions);

const statTypeOptions = computed(() => Object.entries(MATCH_STATS_CONFIG.value.statTypes).map(([key, value]) => ({
  key,
  label: value.name,
})));

const currentColumns = computed(() => {
  const statType = MATCH_STATS_CONFIG.value.statTypes[selectedStats.value];
  return statType ? statType.columns : [];
});

const isMatchTied = computed(() => props.matchData?.a_score === props.matchData?.b_score);

// Sticky winning side: only flips when a team actually takes the lead from the
// other. Ties keep the previous value to avoid the swap thrashing through
// 0-0, 1-1, 2-2, ... during live processing.
const winningSide = ref(null);
watch(
  () => [props.matchData?.a_score, props.matchData?.b_score],
  ([a, b]) => {
    if (a == null || b == null) return;
    if (a > b) winningSide.value = 'a';
    else if (b > a) winningSide.value = 'b';
  },
  { immediate: true },
);

const losingSide = computed(() => {
  if (winningSide.value == null) return null;
  return winningSide.value == 'a' ? 'b' : 'a';
});

// During live processing, every overview can carry the same `side`, in which
// case we can't reliably split — flag that so we fall back to a single
// combined table.
const splitTeams = computed(() => {
  const overviews = props.matchData?.overviews || [];
  const sideA = overviews.filter((p) => p?.side == 'a');
  const sideB = overviews.filter((p) => p?.side == 'b');

  if (sideA.length > 0 && sideB.length > 0) {
    return { a: sideA, b: sideB, reliable: true };
  }
  return { a: overviews, b: [], reliable: false };
});

// Ordered teams for rendering. Keyed by side so TransitionGroup animates a
// position swap when the winning side flips (instead of just re-rendering content).
const orderedTeams = computed(() => {
  // Side data not yet reliable (typical during live processing): render a single
  // combined table rather than guessing a split that won't match the final teams.
  if (!splitTeams.value.reliable) {
    const players = sortPlayers(splitTeams.value.a, currentColumns.value);
    if (players.length === 0) return [];
    return [{
      id: 'all', players, label: escapeHtml(t('coach.match_overview.players')), isWinning: false,
    }];
  }

  const winningId = winningSide.value === 'b' ? 'b' : 'a';
  const losingId = winningId === 'a' ? 'b' : 'a';

  const teamFor = (id) => {
    const players = sortPlayers(splitTeams.value[id], currentColumns.value);
    const isWinning = id === winningId;
    // StatsTable renders the label with v-html; translations come from SimpleLocalize, so escape.
    let label;
    if (winningSide.value === null) {
      label = id === 'a' ? t('coach.match_overview.team_a') : t('coach.match_overview.team_b');
    } else {
      label = isWinning ? t('coach.match_overview.winning_team') : t('coach.match_overview.losing_team');
    }
    return {
      id, players, label: props.sideLabels?.[id] || escapeHtml(label), isWinning,
    };
  };

  const winning = teamFor(winningId);
  const losing = teamFor(losingId);

  const teams = [];
  if (selectedTeams.value === 'all' || selectedTeams.value === 'winningteam') {
    if (winning.players.length > 0) teams.push(winning);
  }
  if (selectedTeams.value === 'all' || selectedTeams.value === 'losingteam') {
    if (losing.players.length > 0) teams.push(losing);
  }
  return teams;
});

// Sorting logic
const sortPlayers = (players, columns) => {
  if (!players) return players;

  return [...players].sort((a, b) => {
    let aValue; let
      bValue;

    // Find the column configuration
    const columnConfig = columns.find((col) => col.key === sortColumn.value);
    if (!columnConfig) {
      aValue = a.refrag_rating?.value ?? 0;
      bValue = b.refrag_rating?.value ?? 0;
    } else {
      aValue = getSortValue(a, columnConfig);
      bValue = getSortValue(b, columnConfig);
    }

    // Handle null/undefined values
    if (aValue === null || aValue === undefined) aValue = 0;
    if (bValue === null || bValue === undefined) bValue = 0;

    // Sort based on direction
    if (sortDirection.value === 'asc') {
      return aValue - bValue;
    }
    return bValue - aValue;
  });
};

function getSortValue(player, column) {
  if (column.key === 'kills' && column.formatter === 'kda') {
    return player.kills?.value ?? 0;
  }

  return player[column.key]?.value ?? 0;
}

// Helper function to determine optimal sort direction for a stat
const getOptimalSortDirection = (columnKey) => {
  // Stats where lower is better (ascending order shows best first)
  const lowerIsBetterStats = [
    'time_to_kill_average',
    'time_to_damage_average',
    'crosshair_placement_average',
    'opening_fails',
    'trade_kill_fails',
    'traded_death_fails',
    'util_flash_friends_flashed',
  ];

  return lowerIsBetterStats.includes(columnKey) ? 'asc' : 'desc';
};

// BACK TO OLD CODE:

// Methods
const updateSelectedTeams = (team) => {
  selectedTeams.value = team;
};

const updateSelectedStats = (stats) => {
  selectedStats.value = stats;
};

const handleColumnSort = (column) => {
  if (sortColumn.value === column) {
    // Toggle direction if same column
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    // Set new column and use optimal sort direction
    sortColumn.value = column;
    sortDirection.value = getOptimalSortDirection(column);
  }
};

const updateTab = (tab) => {
  emit('updateTab', tab);
};

const openServerModal = (selectedStat) => {
  emit('openServerModal', selectedStat);
};

</script>

<style scoped>
.team-swap-move {
  transition: transform 0.45s cubic-bezier(0.4, 0, 0.2, 1);
}
.team-swap-enter-active,
.team-swap-leave-active {
  transition: opacity 0.3s ease;
}
.team-swap-enter-from,
.team-swap-leave-to {
  opacity: 0;
}
.team-swap-leave-active {
  position: absolute;
  width: 100%;
}
</style>
